/**
 * Missing-wall kinds and their validation (PRP-SKETCH-FIELD-2 E13, E14). The
 * table is the policy's `missing_kinds`; no kind name is written here.
 *
 * Stored form (data contract): `missing: { kind, toFloor, toCeiling, sillIn,
 * heightIn, trim }`. A FIELD-1 missing wall has only `trim`: across the whole
 * wall it reads as `legacy_missing_kind` (end to end), across part of it as
 * `legacy_missing_kind_partial` (floor to ceiling, FIELD-1 D4), the same split
 * the backend export makes with `export.fills_wall_tol_in`.
 */
import { IN_PER_FT, POLICY, inToFt } from './policy';
import { dist, edgeOf } from './geom';
import { partnerOf } from './shared';
import type { Fail, MissingKind, MissingWallV2, Opening, Plan, Room } from './types';

export interface MissingOverrides { sillIn?: number; heightIn?: number }

/** Every kind field of a missing wall (FIELD-3's `absorbed` and `removedOpenings` records are separate and optional). */
export type MissingFull = Required<Omit<MissingWallV2, 'absorbed' | 'removedOpenings' | 'recordDropped'>> & Pick<MissingWallV2, 'absorbed' | 'removedOpenings' | 'recordDropped'>;

/** The opening covers its whole wall (within `export.fills_wall_tol_in`, as the export decides it). */
export function fillsWall(r: Room, o: Opening): boolean {
  const E = edgeOf(r, o.edgeStart);
  if (!E) return true;
  const tol = inToFt(POLICY.export.fills_wall_tol_in);
  return o.offFt <= tol && o.offFt + o.widthFt >= dist(E.a, E.b) - tol;
}

/** The kind an opening's missing wall is (a FIELD-1 one reads as a legacy kind by how much wall it covers). */
export const kindOf = (o: Opening, r: Room): MissingKind =>
  o.missing?.kind ?? (fillsWall(r, o) ? POLICY.legacy_missing_kind : POLICY.legacy_missing_kind_partial);

/**
 * A kind's stored fields for a room whose (lower) ceiling is `ceilingFt`. A height
 * that follows the ceiling is ceiling minus sill; editable sill and height take the
 * overrides.
 */
export function missingFields(
  kind: MissingKind, ceilingFt: number, over: MissingOverrides = {}, trim: string = POLICY.missing_wall_default.trim,
): MissingFull {
  const k = POLICY.missing_kinds[kind];
  const ceilIn = ceilingFt * IN_PER_FT;
  const sillIn = k.sill_editable && over.sillIn != null ? over.sillIn : k.sill_in;
  let heightIn: number;
  if (k.height_from_ceiling) heightIn = ceilIn - sillIn;
  else if (k.height_editable && over.heightIn != null) heightIn = over.heightIn;
  else heightIn = k.height_in ?? ceilIn - sillIn;
  return { trim, kind, toFloor: k.to_floor, toCeiling: k.to_ceiling, sillIn, heightIn };
}

/** Every field of room `r`'s missing wall `o`, FIELD-1 ones filled from the legacy kind. */
export function missingOf(o: Opening, r: Room, ceilingFt: number = r.ceilingFt): MissingFull {
  const m = o.missing;
  const base = missingFields(kindOf(o, r), ceilingFt, {}, m?.trim ?? POLICY.missing_wall_default.trim);
  if (!m?.kind) return base;
  return {
    ...base,
    ...Object.fromEntries(Object.entries(m).filter(([, v]) => v != null)),
  } as MissingFull;
}

/** The lower ceiling of the room and the room across the opening, inches (E14). */
export function ceilingLimitIn(plan: Plan, r: Room, o: Opening): number {
  const P = partnerOf(plan, r, o);
  const ft = P ? Math.min(r.ceilingFt, P.r.ceilingFt) : r.ceilingFt;
  return ft * IN_PER_FT;
}

export type LimitFail = Fail<'limit' | 'invalid'> & { limitIn: number };

/**
 * E14. An opening that does not reach the ceiling must stop below the lower
 * ceiling (passthrough: sill plus height; a floor opening: its height); a pony
 * wall's sill must be below it. Returns the limit with a refusal.
 */
export function validateMissing(m: MissingWallV2, limitIn: number): { ok: true } | LimitFail {
  const sill = m.sillIn ?? 0, h = m.heightIn ?? 0;
  const lim = Number(limitIn.toFixed(2));
  if (!Number.isFinite(sill) || !Number.isFinite(h) || sill < 0 || h <= 0) {
    return { ok: false, err: 'invalid', limitIn, message: 'Enter a sill and a height' };
  }
  if (!m.toFloor && m.toCeiling && sill >= limitIn) {
    return { ok: false, err: 'limit', limitIn, message: `The sill must be under ${lim}" (the lower ceiling)` };
  }
  if (!m.toCeiling && sill + h >= limitIn) {
    const what = m.toFloor ? 'The height' : 'Sill plus height';
    return { ok: false, err: 'limit', limitIn, message: `${what} must be under ${lim}" (the lower ceiling)` };
  }
  return { ok: true };
}
